// Lógica pura de "zoom por selección de zona" (marquee zoom) del visor de
// evidencias (POD) en ServiceValidationPods. Se extrae aquí para poder probarla
// sin renderizar el componente (mismo patrón que panHint.js/filterDefaults.js),
// ya que el runner de tests CRA no es fiable sobre Vite.
//
// Modelo del visor: un contenedor con overflow:auto (el "viewport") y un <img>
// cuya caja mide ancho = zoom × visor (sin transform: scale).
// Con origen top-left, las coordenadas de CONTENIDO del viewport
// (scroll + offset dentro del viewport) mapean linealmente a píxeles de la
// imagen base dividiendo por el zoom actual. No se asume 1:1.

// Umbral (px) por debajo del cual una selección se considera un click simple
// (ruido) y no dispara zoom.
const MIN_MARQUEE_PX = 8

// Estado inicial del marquee (se guarda en un ref dentro del componente, no en
// estado React: mutarlo durante el arrastre no debe provocar re-render).
export const initMarquee = () => ({
  dragging: false,
  startX: 0,
  startY: 0,
})

// Al pulsar el ratón: captura el punto inicial en COORDENADAS DE CONTENIDO del
// viewport (offset dentro del viewport + scroll actual), de modo que el
// rectángulo ya incorpora el scroll vigente.
export const beginMarquee = (el, e) => {
  const rect = el.getBoundingClientRect()
  return {
    dragging: true,
    startX: e.clientX - rect.left + el.scrollLeft,
    startY: e.clientY - rect.top + el.scrollTop,
  }
}

// Durante el arrastre: devuelve el rectángulo NORMALIZADO (left/top/width/height
// siempre positivos) en coordenadas de contenido del viewport, listo para
// pintar el overlay. Soporta arrastres en reversa (hacia arriba/izquierda).
export const updateMarquee = (state, el, e) => {
  const rect = el.getBoundingClientRect()
  const curX = e.clientX - rect.left + el.scrollLeft
  const curY = e.clientY - rect.top + el.scrollTop
  const left = Math.min(state.startX, curX)
  const top = Math.min(state.startY, curY)
  const width = Math.abs(curX - state.startX)
  const height = Math.abs(curY - state.startY)
  return { left, top, width, height }
}

const clamp = (v, min, max) => Math.min(Math.max(v, min), max)

// Dado el rectángulo dibujado (coordenadas de contenido del viewport), el tamaño
// visible del viewport (vw×vh), el zoom actual (z0) y los topes de zoom, calcula
// el nuevo zoom y el scroll para que la región seleccionada llene el visor.
//
// Devuelve { zoom, scrollLeft, scrollTop, apply }. Si la selección es demasiado
// pequeña (click simple), `apply` es false y no debe aplicarse.
export const marqueeZoom = ({ rect, viewport, zoom, minZoom = 0.25, maxZoom = 5 }) => {
  if (!rect || rect.width < MIN_MARQUEE_PX || rect.height < MIN_MARQUEE_PX) {
    return { zoom, scrollLeft: 0, scrollTop: 0, apply: false }
  }
  const { vw, vh } = viewport
  const z0 = zoom
  // Región en píxeles de la imagen BASE (sin escalar).
  const imgX = (rect.left) / z0
  const imgY = (rect.top) / z0
  const imgW = rect.width / z0
  const imgH = rect.height / z0
  // Nuevo zoom para que la región llene el visor respetando el aspecto y topes.
  const zNew = clamp(Math.min(vw / imgW, vh / imgH), minZoom, maxZoom)
  // Scroll para centrar la región dentro del viewport con el nuevo zoom.
  const scrollLeft = imgX * zNew - (vw - imgW * zNew) / 2
  const scrollTop = imgY * zNew - (vh - imgH * zNew) / 2
  return {
    zoom: zNew,
    scrollLeft: Math.max(0, scrollLeft),
    scrollTop: Math.max(0, scrollTop),
    apply: true,
  }
}

// Decide si el gesto actual es PAN (secundario) en vez de MARQUEE (por defecto):
// botón central del ratón (e.button === 1), barra espaciadora mantenida o
// tecla Ctrl presionada junto con el arrastre.
export const isPanGesture = (e, spaceHeld) => e.button === 1 || !!spaceHeld || !!e.ctrlKey
